import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp,
  Package,
  Trash2,
  Settings,
  Tag,
  Calendar,
  DollarSign,
  ShoppingCart,
  Clock,
  RefreshCw,
} from 'lucide-react';
import {
  useSalesAnalytics,
  useSalesByModifier,
  useInventoryAnalytics,
  useWasteAnalytics,
  useOperationsAnalytics,
  usePromotionAnalytics,
} from '../../api/queries/analytics.queries';
import {
  SalesByHourChart,
  SalesByCategoryChart,
  TopSellingItemsChart,
  PaymentMethodsChart,
  ModifierDistributionChart,
} from './components/SalesCharts';
import {
  StockStatusChart,
  IngredientUsageChart,
  StockLevelsChart,
} from './components/InventoryCharts';
import {
  WasteTrendChart,
  WasteByCategoryChart,
  WasteByIngredientChart,
} from './components/WasteCharts';
import NLQChat from './components/NLQChat';

type TabType = 'sales' | 'inventory' | 'waste' | 'operations' | 'promotions';

function AnalyticsPage() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('sales');
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'year'>('today');

  // Calculate date range
  const getDateRange = () => {
    const end = new Date();
    const start = new Date();

    switch (dateRange) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        break;
      case 'week':
        start.setDate(start.getDate() - 7);
        break;
      case 'month':
        start.setMonth(start.getMonth() - 1);
        break;
      case 'year':
        start.setFullYear(start.getFullYear() - 1);
        break;
    }

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  };

  const { startDate, endDate } = getDateRange();

  // Fetch analytics data
  const { data: salesMetrics, refetch: refetchSales } = useSalesAnalytics(undefined, startDate, endDate);
  const { data: salesByModifier, refetch: refetchModifiers } = useSalesByModifier(undefined, startDate, endDate);
  const { data: inventoryMetrics, refetch: refetchInventory } = useInventoryAnalytics();
  const { data: wasteMetrics, refetch: refetchWaste } = useWasteAnalytics(undefined, startDate, endDate);
  const { data: operationsMetrics, refetch: refetchOps } = useOperationsAnalytics(undefined, startDate, endDate);
  const { data: promotionMetrics, refetch: refetchPromo } = usePromotionAnalytics(undefined, startDate, endDate);

  const handleRefresh = () => {
    refetchSales();
    refetchModifiers();
    refetchInventory();
    refetchWaste();
    refetchOps();
    refetchPromo();
  };

  const getLangValue = (multiLang: any) => {
    return multiLang?.[i18n.language as 'en' | 'th'] || multiLang?.en || '';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
    }).format(amount);
  };

  const tabs = [
    { id: 'sales' as TabType, label: t('analytics.sales'), icon: TrendingUp },
    { id: 'inventory' as TabType, label: t('analytics.inventory'), icon: Package },
    { id: 'waste' as TabType, label: t('analytics.waste'), icon: Trash2 },
    { id: 'operations' as TabType, label: t('analytics.operations'), icon: Settings },
    { id: 'promotions' as TabType, label: t('promotions.title'), icon: Tag },
  ];

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b bg-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('analytics.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('analytics.subtitle')}
            </p>
          </div>
          <button
            onClick={handleRefresh}
            className="p-2 rounded-lg hover:bg-secondary transition-colors"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="border-b bg-card p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Tab Navigation */}
          <div className="flex items-center gap-1 bg-secondary/30 rounded-lg p-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-secondary'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as any)}
            className="rounded-lg border bg-background px-3 py-1.5 text-sm"
          >
            <option value="today">{t('analytics.today')}</option>
            <option value="week">{t('analytics.lastWeek')}</option>
            <option value="month">{t('analytics.lastMonth')}</option>
            <option value="year">{t('analytics.lastYear')}</option>
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Sales Analytics */}
          {activeTab === 'sales' && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-card rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('analytics.totalRevenue')}</p>
                      <p className="text-2xl font-bold mt-1">
                        {formatCurrency(salesMetrics?.totalRevenue || 0)}
                      </p>
                    </div>
                    <DollarSign className="h-8 w-8 text-green-500" />
                  </div>
                </div>

                <div className="bg-card rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('analytics.totalOrders')}</p>
                      <p className="text-2xl font-bold mt-1">{salesMetrics?.totalOrders || 0}</p>
                    </div>
                    <ShoppingCart className="h-8 w-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-card rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t('analytics.averageOrderValue')}
                      </p>
                      <p className="text-2xl font-bold mt-1">
                        {formatCurrency(salesMetrics?.averageOrderValue || 0)}
                      </p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-purple-500" />
                  </div>
                </div>

                <div className="bg-card rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('analytics.peakHour')}</p>
                      <p className="text-2xl font-bold mt-1">
                        {salesMetrics?.salesByHour?.[0]?.hour || '-'}:00
                      </p>
                    </div>
                    <Clock className="h-8 w-8 text-orange-500" />
                  </div>
                </div>
              </div>

              {/* Sales by Hour Chart */}
              <div className="bg-card rounded-lg border p-4">
                <h3 className="font-semibold mb-4">{t('analytics.salesByHour')}</h3>
                <SalesByHourChart data={salesMetrics?.salesByHour} />
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Top Selling Items */}
                <div className="bg-card rounded-lg border p-4">
                  <h3 className="font-semibold mb-4">{t('analytics.topSellingItems')}</h3>
                  <TopSellingItemsChart data={salesMetrics?.topSellingItems} />
                </div>

                {/* Sales by Category */}
                <div className="bg-card rounded-lg border p-4">
                  <h3 className="font-semibold mb-4">{t('analytics.salesByCategory')}</h3>
                  <SalesByCategoryChart data={salesMetrics?.salesByCategory} />
                </div>
              </div>

              {/* Modifiers Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-card rounded-lg border p-4">
                  <h3 className="font-semibold mb-4">{t('analytics.bySweetness')}</h3>
                  <ModifierDistributionChart data={salesByModifier?.sweetness} type="sweetness" />
                </div>

                <div className="bg-card rounded-lg border p-4">
                  <h3 className="font-semibold mb-4">{t('analytics.bySize')}</h3>
                  <ModifierDistributionChart data={salesByModifier?.sizes} type="size" />
                </div>

                <div className="bg-card rounded-lg border p-4">
                  <h3 className="font-semibold mb-4">{t('analytics.paymentMethods')}</h3>
                  <PaymentMethodsChart data={salesMetrics?.paymentMethods} />
                </div>
              </div>

              {/* Top Toppings */}
              <div className="bg-card rounded-lg border p-4">
                <h3 className="font-semibold mb-4">{t('analytics.topToppings')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {salesByModifier?.toppings?.slice(0, 10).map((item) => (
                    <div
                      key={item.topping}
                      className="p-3 bg-secondary/30 rounded text-center"
                    >
                      <p className="text-sm font-medium">{item.topping}</p>
                      <p className="text-lg font-bold">{item.count}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Inventory Analytics */}
          {activeTab === 'inventory' && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-card rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('inventory.totalItems')}</p>
                      <p className="text-2xl font-bold mt-1">
                        {inventoryMetrics?.totalItems || 0}
                      </p>
                    </div>
                    <Package className="h-8 w-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-card rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('inventory.lowStock')}</p>
                      <p className="text-2xl font-bold mt-1 text-orange-500">
                        {inventoryMetrics?.lowStockCount || 0}
                      </p>
                    </div>
                    <Package className="h-8 w-8 text-orange-500" />
                  </div>
                </div>

                <div className="bg-card rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('inventory.expiringSoon')}</p>
                      <p className="text-2xl font-bold mt-1 text-red-500">
                        {inventoryMetrics?.expiringCount || 0}
                      </p>
                    </div>
                    <Clock className="h-8 w-8 text-red-500" />
                  </div>
                </div>

                <div className="bg-card rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t('analytics.inventoryValue')}
                      </p>
                      <p className="text-2xl font-bold mt-1">
                        {formatCurrency(inventoryMetrics?.totalValue || 0)}
                      </p>
                    </div>
                    <DollarSign className="h-8 w-8 text-green-500" />
                  </div>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card rounded-lg border p-4">
                  <h3 className="font-semibold mb-4">{t('analytics.stockStatus')}</h3>
                  <StockStatusChart data={inventoryMetrics?.stockLevels} />
                </div>

                <div className="bg-card rounded-lg border p-4">
                  <h3 className="font-semibold mb-4">{t('analytics.ingredientUsage')}</h3>
                  <IngredientUsageChart data={inventoryMetrics?.usageByIngredient} />
                </div>
              </div>

              {/* Stock Levels */}
              <div className="bg-card rounded-lg border p-4">
                <h3 className="font-semibold mb-4">{t('analytics.stockLevels')}</h3>
                <StockLevelsChart data={inventoryMetrics?.stockLevels} />
              </div>

              {/* Stock Levels Table */}
              <div className="bg-card rounded-lg border p-4">
                <h3 className="font-semibold mb-4">{t('analytics.stockDetails')}</h3>
                <div className="space-y-2">
                  {inventoryMetrics?.stockLevels?.map((item) => (
                    <div
                      key={item.ingredientId}
                      className="flex items-center justify-between p-3 bg-secondary/30 rounded"
                    >
                      <div className="flex-1">
                        <p className="font-medium">{item.ingredientName}</p>
                        <div className="mt-2">
                          <div className="h-2 bg-secondary rounded-full overflow-hidden">
                            <div
                              className={`h-full ${
                                item.status === 'low' || item.status === 'out_of_stock'
                                  ? 'bg-red-500'
                                  : item.status === 'overstock'
                                  ? 'bg-orange-500'
                                  : 'bg-green-500'
                              }`}
                              style={{
                                width: `${Math.min(
                                  (item.currentStock / item.maxLevel) * 100,
                                  100
                                )}%`,
                              }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {item.currentStock} / {item.maxLevel} (Reorder: {item.reorderLevel})
                          </p>
                        </div>
                      </div>
                      <span
                        className={`ml-4 px-2 py-1 text-xs rounded ${
                          item.status === 'low'
                            ? 'bg-orange-100 text-orange-700'
                            : item.status === 'out_of_stock'
                            ? 'bg-red-100 text-red-700'
                            : item.status === 'overstock'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {t(`inventory.${item.status}`)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Waste Analytics */}
          {activeTab === 'waste' && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-card rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('waste.totalValue')}</p>
                      <p className="text-2xl font-bold mt-1 text-red-500">
                        {formatCurrency(wasteMetrics?.totalValue || 0)}
                      </p>
                    </div>
                    <DollarSign className="h-8 w-8 text-red-500" />
                  </div>
                </div>

                <div className="bg-card rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('waste.totalEntries')}</p>
                      <p className="text-2xl font-bold mt-1">{wasteMetrics?.totalEntries || 0}</p>
                    </div>
                    <Trash2 className="h-8 w-8 text-gray-500" />
                  </div>
                </div>

                <div className="bg-card rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t('analytics.averageWasteValue')}
                      </p>
                      <p className="text-2xl font-bold mt-1">
                        {formatCurrency(
                          wasteMetrics?.totalEntries
                            ? wasteMetrics.totalValue / wasteMetrics.totalEntries
                            : 0
                        )}
                      </p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-purple-500" />
                  </div>
                </div>
              </div>

              {/* Waste Trend Chart */}
              <div className="bg-card rounded-lg border p-4">
                <h3 className="font-semibold mb-4">{t('analytics.wasteTrend')}</h3>
                <WasteTrendChart data={wasteMetrics?.trend} />
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card rounded-lg border p-4">
                  <h3 className="font-semibold mb-4">{t('waste.byCategory')}</h3>
                  <WasteByCategoryChart data={wasteMetrics?.byCategory} />
                </div>

                <div className="bg-card rounded-lg border p-4">
                  <h3 className="font-semibold mb-4">{t('analytics.wasteByIngredient')}</h3>
                  <WasteByIngredientChart data={wasteMetrics?.byIngredient} />
                </div>
              </div>

              {/* Waste Details Table */}
              <div className="bg-card rounded-lg border p-4">
                <h3 className="font-semibold mb-4">{t('analytics.wasteDetails')}</h3>
                <div className="space-y-2">
                  {wasteMetrics?.byIngredient?.map((item) => (
                    <div
                      key={item.ingredientId}
                      className="flex items-center justify-between p-3 bg-secondary/30 rounded"
                    >
                      <div>
                        <p className="font-medium">{item.ingredientName}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.quantity} {item.unit}
                        </p>
                      </div>
                      <p className="font-semibold text-red-600">{formatCurrency(item.value)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Operations Analytics */}
          {activeTab === 'operations' && (
            <>
              {/* Purchase Requests */}
              <div className="bg-card rounded-lg border p-4">
                <h3 className="font-semibold mb-4">{t('purchasing.purchaseRequests')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-secondary/30 rounded-lg">
                    <p className="text-3xl font-bold">{operationsMetrics?.purchaseRequests?.total || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t('common.all')}</p>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <p className="text-3xl font-bold text-orange-500">
                      {operationsMetrics?.purchaseRequests?.pending || 0}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{t('purchasing.pending')}</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-3xl font-bold text-green-500">
                      {operationsMetrics?.purchaseRequests?.approved || 0}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{t('purchasing.approved')}</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <p className="text-3xl font-bold text-red-500">
                      {operationsMetrics?.purchaseRequests?.rejected || 0}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{t('purchasing.rejected')}</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-500">
                      {operationsMetrics?.purchaseRequests?.averageApprovalTime?.toFixed(1) || 0}h
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{t('analytics.avgApprovalTime')}</p>
                  </div>
                </div>
              </div>

              {/* Purchase Orders */}
              <div className="bg-card rounded-lg border p-4">
                <h3 className="font-semibold mb-4">{t('purchasing.purchaseOrders')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-secondary/30 rounded-lg">
                    <p className="text-3xl font-bold">{operationsMetrics?.purchaseOrders?.total || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t('common.all')}</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold">{operationsMetrics?.purchaseOrders?.draft || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t('purchasing.draft')}</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-500">{operationsMetrics?.purchaseOrders?.sent || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t('purchasing.sent')}</p>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <p className="text-3xl font-bold text-orange-500">
                      {operationsMetrics?.purchaseOrders?.partiallyReceived || 0}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('purchasing.partiallyReceived')}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-3xl font-bold text-green-500">
                      {operationsMetrics?.purchaseOrders?.completed || 0}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{t('purchasing.completed')}</p>
                  </div>
                </div>
              </div>

              {/* Transfers */}
              <div className="bg-card rounded-lg border p-4">
                <h3 className="font-semibold mb-4">{t('transfers.title')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-secondary/30 rounded-lg">
                    <p className="text-3xl font-bold">{operationsMetrics?.transfers?.total || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t('common.all')}</p>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <p className="text-3xl font-bold text-orange-500">
                      {operationsMetrics?.transfers?.requested || 0}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{t('transfers.requested')}</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-500">
                      {operationsMetrics?.transfers?.inTransit || 0}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{t('transfers.inTransit')}</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-3xl font-bold text-green-500">
                      {operationsMetrics?.transfers?.received || 0}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{t('transfers.received')}</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-3xl font-bold text-purple-500">
                      {operationsMetrics?.transfers?.averageDeliveryTime?.toFixed(1) || 0}h
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{t('analytics.avgDeliveryTime')}</p>
                  </div>
                </div>
              </div>

              {/* Kitchen Performance */}
              <div className="bg-card rounded-lg border p-4">
                <h3 className="font-semibold mb-4">{t('analytics.kitchenPerformance')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="text-center p-6 bg-secondary/30 rounded-lg">
                    <p className="text-4xl font-bold">
                      {operationsMetrics?.kitchenPerformance?.averagePrepTime?.toFixed(1) || 0}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      {t('analytics.avgPrepTime')} (min)
                    </p>
                  </div>
                  <div className="text-center p-6 bg-secondary/30 rounded-lg">
                    <p className="text-4xl font-bold">
                      {operationsMetrics?.kitchenPerformance?.ordersCompleted || 0}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      {t('analytics.ordersCompleted')}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Promotion Analytics */}
          {activeTab === 'promotions' && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-card rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t('analytics.activePromotions')}
                      </p>
                      <p className="text-2xl font-bold mt-1">
                        {promotionMetrics?.activePromotions || 0}
                      </p>
                    </div>
                    <Tag className="h-8 w-8 text-green-500" />
                  </div>
                </div>

                <div className="bg-card rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t('analytics.totalDiscountGiven')}
                      </p>
                      <p className="text-2xl font-bold mt-1 text-red-500">
                        {formatCurrency(promotionMetrics?.totalDiscountGiven || 0)}
                      </p>
                    </div>
                    <DollarSign className="h-8 w-8 text-red-500" />
                  </div>
                </div>

                <div className="bg-card rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t('analytics.avgDiscountPerOrder')}
                      </p>
                      <p className="text-2xl font-bold mt-1">
                        {formatCurrency(
                          salesMetrics?.totalOrders
                            ? (promotionMetrics?.totalDiscountGiven || 0) /
                                salesMetrics.totalOrders
                            : 0
                        )}
                      </p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-purple-500" />
                  </div>
                </div>
              </div>

              {/* Usage by Promotion */}
              <div className="bg-card rounded-lg border p-4">
                <h3 className="font-semibold mb-4">{t('analytics.promotionPerformance')}</h3>
                <div className="space-y-3">
                  {promotionMetrics?.usageByPromotion?.map((promo) => (
                    <div
                      key={promo.promotionId}
                      className="p-4 bg-secondary/30 rounded-lg"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium">{getLangValue(promo.promotionName)}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            <span>{promo.usageCount} {t('analytics.uses')}</span>
                            <span>|</span>
                            <span>{t('analytics.discount')}: {formatCurrency(promo.totalDiscount)}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold">{formatCurrency(promo.revenueImpact)}</p>
                          <p className="text-xs text-muted-foreground">{t('analytics.revenueImpact')}</p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="h-2 bg-secondary rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary"
                            style={{
                              width: `${Math.min((promo.usageCount / 100) * 100, 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!promotionMetrics?.usageByPromotion || promotionMetrics.usageByPromotion.length === 0) && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Tag className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>{t('analytics.noPromotionData')}</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* NLQ Chat */}
      <NLQChat />
    </div>
  );
}

export default AnalyticsPage;
